import { FinnairBoundItem } from '@fcom/dapi/api/models/finnair-bound-item';
import { FinnairCheckInEligibility } from '@fcom/dapi/api/models/finnair-check-in-eligibility';
import { FinnairOrder } from '@fcom/dapi/api/models/finnair-order';
import { TzDate } from '@fcom/core/utils/tz-date';

import { isFlight } from './flight.utils';

const AIRPORT_WINDOW_HOURS = 5;
const ARRIVAL_WINDOW_HOURS = 4;
const PREPARATION_START = TzDate.of('1970-01-01T00:00:00Z');

export enum FinnairOrderPhase {
  PREPARATION = 'Preparation',
  CHECK_IN = 'Check-in',
  AIRPORT = 'Airport',
  FLIGHT = 'Flight',
  ARRIVAL = 'Arrival',
  AFTER = 'After',
}

interface OrderSegment {
  arrival: TzDate;
  departure: TzDate;
  id: string;
  index: number;
}

interface OrderBound {
  arrival: TzDate;
  bound: FinnairBoundItem;
  departure: TzDate;
  index: number;
  segments: OrderSegment[];
}

interface OrderBoundWindow {
  arrivalEnd: TzDate;
  arrivalStart: TzDate;
  bound: OrderBound;
  checkInEnd?: TzDate;
  checkInStart?: TzDate;
  preparationEnd: TzDate;
  preparationStart: TzDate;
}

interface PhaseWindow {
  boundIndex: number;
  endsAt?: TzDate;
  phase: FinnairOrderPhase;
  priority: number;
  startsAt: TzDate;
}

const isDefined = <T>(value: T | undefined): value is T => value !== undefined;

const PHASE_PRIORITY: Record<FinnairOrderPhase, number> = {
  [FinnairOrderPhase.PREPARATION]: 1,
  [FinnairOrderPhase.ARRIVAL]: 2,
  [FinnairOrderPhase.AIRPORT]: 3,
  [FinnairOrderPhase.CHECK_IN]: 4,
  [FinnairOrderPhase.FLIGHT]: 5,
  [FinnairOrderPhase.AFTER]: 0,
};

const getDate = (value: string | TzDate): TzDate => (value instanceof TzDate ? value : TzDate.of(value));

const isWindowActiveAt = (window: PhaseWindow, at: TzDate): boolean => {
  return window.startsAt.millis <= at.millis && (!window.endsAt || at.millis < window.endsAt.millis);
};

const minDate = (left: TzDate, right: TzDate): TzDate => (left.millis <= right.millis ? left : right);

const toOrderSegments = (bound: FinnairBoundItem): OrderSegment[] => {
  const flights = bound.itinerary?.filter(isFlight) ?? [];

  if (flights.length === 0) {
    return [
      {
        arrival: TzDate.of(bound.arrival.dateTime),
        departure: TzDate.of(bound.departure.dateTime),
        id: bound.id,
        index: 0,
      },
    ];
  }

  return flights.map((flight, index) => ({
    arrival: TzDate.of(flight.arrival.dateTime),
    departure: TzDate.of(flight.departure.dateTime),
    id: flight.id,
    index,
  }));
};

const toOrderBounds = (order: FinnairOrder): OrderBound[] => {
  return (order.bounds ?? [])
    .map((bound, index) => {
      const segments = toOrderSegments(bound);

      return {
        arrival: segments[segments.length - 1].arrival,
        bound,
        departure: segments[0].departure,
        index,
        segments,
      };
    })
    .sort((left, right) => left.departure.millis - right.departure.millis)
    .map((bound, index) => ({
      ...bound,
      index,
    }));
};

const getCheckInEligibility = (order: FinnairOrder, boundId: string): FinnairCheckInEligibility | undefined => {
  return order.eligibilities?.checkIn?.find((eligibility) => eligibility.id === boundId);
};

const toOrderBoundWindows = (order: FinnairOrder): OrderBoundWindow[] => {
  const bounds = toOrderBounds(order);

  return bounds.map((bound, index) => {
    const previousBound = index > 0 ? bounds[index - 1] : undefined;
    const checkInWindow = getCheckInEligibility(order, bound.bound.id)?.checkinWindow;
    const checkInStart = checkInWindow?.start ? TzDate.of(checkInWindow.start) : undefined;
    const airportStart = bound.departure.removeHours(AIRPORT_WINDOW_HOURS);
    const checkInEnd = checkInWindow?.end ? minDate(TzDate.of(checkInWindow.end), airportStart) : airportStart;

    return {
      arrivalEnd: bound.arrival.addHours(ARRIVAL_WINDOW_HOURS),
      arrivalStart: bound.arrival,
      bound,
      checkInEnd,
      checkInStart,
      preparationEnd: checkInStart ?? airportStart,
      preparationStart: previousBound?.arrival ?? PREPARATION_START,
    };
  });
};

const createPhaseWindow = (
  phase: FinnairOrderPhase,
  startsAt: TzDate,
  endsAt: TzDate | undefined,
  boundIndex: number
): PhaseWindow | undefined => {
  if (endsAt && startsAt.millis >= endsAt.millis) {
    return undefined;
  }

  return {
    boundIndex,
    endsAt,
    phase,
    priority: PHASE_PRIORITY[phase],
    startsAt,
  };
};

const buildBoundWindows = (boundWindow: OrderBoundWindow): PhaseWindow[] => {
  const airportWindows = boundWindow.bound.segments
    .map((segment, index, segments) =>
      createPhaseWindow(
        FinnairOrderPhase.AIRPORT,
        index === 0 ? segment.departure.removeHours(AIRPORT_WINDOW_HOURS) : segments[index - 1].arrival,
        segment.departure,
        boundWindow.bound.index
      )
    )
    .filter(isDefined);

  const flightWindows = boundWindow.bound.segments
    .map((segment) =>
      createPhaseWindow(FinnairOrderPhase.FLIGHT, segment.departure, segment.arrival, boundWindow.bound.index)
    )
    .filter(isDefined);

  return [
    createPhaseWindow(
      FinnairOrderPhase.PREPARATION,
      boundWindow.preparationStart,
      boundWindow.preparationEnd,
      boundWindow.bound.index
    ),
    boundWindow.checkInStart
      ? createPhaseWindow(
          FinnairOrderPhase.CHECK_IN,
          boundWindow.checkInStart,
          boundWindow.checkInEnd,
          boundWindow.bound.index
        )
      : undefined,
    ...airportWindows,
    ...flightWindows,
    createPhaseWindow(
      FinnairOrderPhase.ARRIVAL,
      boundWindow.arrivalStart,
      boundWindow.arrivalEnd,
      boundWindow.bound.index
    ),
  ].filter(isDefined);
};

const getPreferredBoundIndex = (
  matchingWindows: PhaseWindow[],
  boundWindows: OrderBoundWindow[],
  at: TzDate
): number | undefined => {
  if (matchingWindows.length === 0) {
    return undefined;
  }

  const matchingBoundIndexes = [...new Set(matchingWindows.map((window) => window.boundIndex))];
  const notFullyFlownBoundIndexes = matchingBoundIndexes.filter(
    (boundIndex) => at.millis < boundWindows[boundIndex].arrivalStart.millis
  );

  if (notFullyFlownBoundIndexes.length > 0) {
    return Math.min(...notFullyFlownBoundIndexes);
  }

  return Math.max(...matchingBoundIndexes);
};

export const getFinnairOrderPhase = (order: FinnairOrder, at: string | TzDate = TzDate.now()): FinnairOrderPhase => {
  const referenceDate = getDate(at);
  const boundWindows = toOrderBoundWindows(order);
  const matchingWindows = boundWindows
    .flatMap((boundWindow) => buildBoundWindows(boundWindow))
    .filter((window) => isWindowActiveAt(window, referenceDate));
  const preferredBoundIndex = getPreferredBoundIndex(matchingWindows, boundWindows, referenceDate);

  if (preferredBoundIndex === undefined) {
    return FinnairOrderPhase.AFTER;
  }

  const preferredWindow = matchingWindows.find((window) => window.boundIndex === preferredBoundIndex);

  return preferredWindow?.phase ?? FinnairOrderPhase.AFTER;
};
